import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const FinesPage = () => {
  const [fines, setFines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingFine, setEditingFine] = useState(null);
  const [selectedFine, setSelectedFine] = useState(null);
  const [filters, setFilters] = useState({ status: "", vehicle_id: "", employee_id: "" });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.vehicle_id) params.append("vehicle_id", filters.vehicle_id);
      if (filters.employee_id) params.append("employee_id", filters.employee_id);
      
      const [finesRes, vehiclesRes, employeesRes] = await Promise.all([
        api.get(`/hr/vehicle-fines?${params.toString()}`),
        api.get("/hr/vehicles"),
        api.get("/employees")
      ]);
      
      setFines(finesRes.data);
      setVehicles(vehiclesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingFine) {
        await api.put(`/hr/vehicle-fines/${editingFine.fine_id}`, data);
        toast.success("Coima atualizada com sucesso");
      } else {
        await api.post("/hr/vehicle-fines", data);
        toast.success("Coima registada com sucesso");
      }
      setShowModal(false);
      setEditingFine(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar coima");
    }
  };

  const handleDelete = async (fineId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta coima? Esta ação é irreversível.")) {
      return;
    }
    try {
      await api.delete(`/hr/vehicle-fines/${fineId}`);
      toast.success("Coima eliminada com sucesso");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar coima");
    }
  };

  const handleUploadDocument = async (fineId, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "auto_noticia");
      
      await api.post(`/hr/vehicle-fines/${fineId}/upload-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Documento carregado com sucesso");
      
      // Refresh fine details
      if (selectedFine?.fine_id === fineId) {
        const res = await api.get(`/hr/vehicle-fines/${fineId}`);
        setSelectedFine(res.data);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar documento");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (fineId, documentId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este documento?")) return;
    
    try {
      await api.delete(`/hr/vehicle-fines/${fineId}/documents/${documentId}`);
      toast.success("Documento eliminado");
      
      // Refresh fine details
      if (selectedFine?.fine_id === fineId) {
        const res = await api.get(`/hr/vehicle-fines/${fineId}`);
        setSelectedFine(res.data);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar documento");
    }
  };

  const handleViewDocument = async (fineId, document) => {
    try {
      const response = await api.get(`/hr/vehicle-fines/${fineId}/documents/${document.document_id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: document.mime_type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Erro ao abrir documento");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_response: { label: "Pendente Resposta", color: "bg-amber-100 text-amber-700" },
      awaiting_payment: { label: "Aguarda Pagamento", color: "bg-blue-100 text-blue-700" },
      salary_deduction_pending: { label: "Dedução Salarial", color: "bg-orange-100 text-orange-700" },
      paid: { label: "Pago", color: "bg-emerald-100 text-emerald-700" },
      contested: { label: "Contestado", color: "bg-purple-100 text-purple-700" },
      cancelled: { label: "Anulado", color: "bg-slate-100 text-slate-700" }
    };
    const config = statusConfig[status] || { label: status, color: "bg-gray-100 text-gray-700" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getTotalPending = () => {
    return fines.filter(f => ["pending_response", "awaiting_payment", "salary_deduction_pending"].includes(f.status))
      .reduce((sum, f) => sum + (f.amount || 0), 0);
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
      
      const response = await api.get(`/hr/vehicle-fines/export/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coimas_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
      
      const response = await api.get(`/hr/vehicle-fines/export/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coimas_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Gestão de Coimas</h1>
          <p className="text-slate-500 dark:text-slate-400">Gerir coimas e multas de viaturas da empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            data-testid="export-fines-excel-btn"
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar para Excel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>
          <button
            onClick={handleExportPDF}
            data-testid="export-fines-pdf-btn"
            className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar para PDF"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            PDF
          </button>
          <button
            onClick={() => { setEditingFine(null); setShowModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            data-testid="add-fine-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Registar Coima
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">Total de Coimas</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white">{fines.length}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">Pendentes</p>
          <p className="text-2xl font-bold text-amber-600">{fines.filter(f => f.status === "pending_response").length}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">Valor Pendente</p>
          <p className="text-2xl font-bold text-red-600">{getTotalPending().toFixed(2)} €</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">Pagas</p>
          <p className="text-2xl font-bold text-emerald-600">{fines.filter(f => f.status === "paid").length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={e => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="pending_response">Pendente Resposta</option>
              <option value="awaiting_payment">Aguarda Pagamento</option>
              <option value="salary_deduction_pending">Dedução Salarial</option>
              <option value="paid">Pago</option>
              <option value="contested">Contestado</option>
              <option value="cancelled">Anulado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Viatura</label>
            <select
              value={filters.vehicle_id}
              onChange={e => setFilters({...filters, vehicle_id: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
            >
              <option value="">Todas</option>
              {vehicles.map(v => (
                <option key={v.vehicle_id} value={v.vehicle_id}>{v.license_plate} - {v.brand} {v.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Colaborador</label>
            <select
              value={filters.employee_id}
              onChange={e => setFilters({...filters, employee_id: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
            >
              <option value="">Todos</option>
              {employees.map(e => (
                <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: "", vehicle_id: "", employee_id: "" })}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Fines Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Viatura</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Infração</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Condutor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Docs</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {fines.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    <svg className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Sem coimas registadas
                  </td>
                </tr>
              ) : (
                fines.map(fine => (
                  <tr key={fine.fine_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800 dark:text-white">{fine.fine_date || "-"}</p>
                      <p className="text-xs text-slate-500">{fine.fine_time || ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-mono font-bold text-blue-600">{fine.license_plate}</p>
                      <p className="text-xs text-slate-500">{fine.vehicle_info?.brand} {fine.vehicle_info?.model}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800 dark:text-white">{fine.infraction_type || "Outra"}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[200px]">{fine.infraction_description}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800 dark:text-white">{fine.assigned_driver_name || "-"}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-800 dark:text-white">{fine.amount?.toFixed(2) || "0.00"} €</p>
                      {fine.points_deducted > 0 && (
                        <p className="text-xs text-red-500">-{fine.points_deducted} pontos</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(fine.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${fine.documents?.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {fine.documents?.length || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setSelectedFine(fine); setShowDetailModal(true); }}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Ver Detalhes"
                          data-testid={`view-fine-${fine.fine_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => { setEditingFine(fine); setShowModal(true); }}
                          className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                          title="Editar"
                          data-testid={`edit-fine-${fine.fine_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(fine.fine_id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Eliminar"
                          data-testid={`delete-fine-${fine.fine_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <FineFormModal
          fine={editingFine}
          vehicles={vehicles}
          employees={employees}
          onClose={() => { setShowModal(false); setEditingFine(null); }}
          onSave={handleSave}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFine && (
        <FineDetailModal
          fine={selectedFine}
          onClose={() => { setShowDetailModal(false); setSelectedFine(null); }}
          onUpload={handleUploadDocument}
          onDeleteDocument={handleDeleteDocument}
          onViewDocument={handleViewDocument}
          uploading={uploading}
          fileInputRef={fileInputRef}
        />
      )}
    </div>
  );
};

// Fine Form Modal Component
const FineFormModal = ({ fine, vehicles, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    vehicle_id: fine?.vehicle_id || "",
    assigned_driver_id: fine?.assigned_driver_id || "",
    fine_date: fine?.fine_date || "",
    fine_time: fine?.fine_time || "",
    infraction_type: fine?.infraction_type || "",
    infraction_description: fine?.infraction_description || "",
    location: fine?.location || "",
    notification_number: fine?.notification_number || "",
    authority: fine?.authority || "",
    amount: fine?.amount || "",
    points_deducted: fine?.points_deducted || 0,
    payment_deadline: fine?.payment_deadline || "",
    status: fine?.status || "pending_response",
    notes: fine?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  const infractionTypes = [
    { value: "speeding", label: "Excesso de Velocidade" },
    { value: "parking", label: "Estacionamento Indevido" },
    { value: "red_light", label: "Sinal Vermelho" },
    { value: "phone", label: "Uso de Telemóvel" },
    { value: "seatbelt", label: "Cinto de Segurança" },
    { value: "alcohol", label: "Álcool/Drogas" },
    { value: "documentation", label: "Documentação" },
    { value: "other", label: "Outra" }
  ];

  const authorities = [
    { value: "GNR", label: "GNR" },
    { value: "PSP", label: "PSP" },
    { value: "EMEL", label: "EMEL" },
    { value: "CM", label: "Câmara Municipal" },
    { value: "ANSR", label: "ANSR" },
    { value: "Other", label: "Outra" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_id) {
      toast.error("Selecione uma viatura");
      return;
    }
    if (!formData.fine_date) {
      toast.error("Indique a data da coima");
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        points_deducted: parseInt(formData.points_deducted) || 0
      });
    } finally {
      setSaving(false);
    }
  };

  // Auto-fill driver when vehicle is selected
  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    setFormData({
      ...formData,
      vehicle_id: vehicleId,
      assigned_driver_id: vehicle?.assigned_to || ""
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">
            {fine ? "Editar Coima" : "Registar Nova Coima"}
          </h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Vehicle & Driver */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Viatura *</label>
                <select
                  value={formData.vehicle_id}
                  onChange={e => handleVehicleChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  required
                  disabled={!!fine}
                >
                  <option value="">Selecionar...</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>{v.license_plate} - {v.brand} {v.model}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Condutor Atribuído</label>
                <select
                  value={formData.assigned_driver_id}
                  onChange={e => setFormData({...formData, assigned_driver_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                >
                  <option value="">Selecionar...</option>
                  {employees.map(e => (
                    <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Data da Coima *</label>
                <input
                  type="date"
                  value={formData.fine_date}
                  onChange={e => setFormData({...formData, fine_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Hora</label>
                <input
                  type="time"
                  value={formData.fine_time}
                  onChange={e => setFormData({...formData, fine_time: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                />
              </div>
            </div>

            {/* Infraction Type & Authority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tipo de Infração</label>
                <select
                  value={formData.infraction_type}
                  onChange={e => setFormData({...formData, infraction_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                >
                  <option value="">Selecionar...</option>
                  {infractionTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Entidade Autuante</label>
                <select
                  value={formData.authority}
                  onChange={e => setFormData({...formData, authority: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                >
                  <option value="">Selecionar...</option>
                  {authorities.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição da Infração</label>
              <textarea
                value={formData.infraction_description}
                onChange={e => setFormData({...formData, infraction_description: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white resize-none"
                rows={2}
                placeholder="Descreva a infração..."
              />
            </div>

            {/* Location & Notification Number */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Local</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={e => setFormData({...formData, location: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  placeholder="Local da infração"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nº Notificação</label>
                <input
                  type="text"
                  value={formData.notification_number}
                  onChange={e => setFormData({...formData, notification_number: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  placeholder="Número do auto"
                />
              </div>
            </div>

            {/* Amount & Points */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Valor (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={e => setFormData({...formData, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Pontos a Deduzir</label>
                <input
                  type="number"
                  min="0"
                  max="12"
                  value={formData.points_deducted}
                  onChange={e => setFormData({...formData, points_deducted: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Prazo Pagamento</label>
                <input
                  type="date"
                  value={formData.payment_deadline}
                  onChange={e => setFormData({...formData, payment_deadline: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                />
              </div>
            </div>

            {/* Status (only for editing) */}
            {fine && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Estado</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                >
                  <option value="pending_response">Pendente Resposta</option>
                  <option value="awaiting_payment">Aguarda Pagamento</option>
                  <option value="salary_deduction_pending">Dedução Salarial Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="contested">Contestado</option>
                  <option value="cancelled">Anulado</option>
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white resize-none"
                rows={2}
                placeholder="Observações adicionais..."
              />
            </div>
          </div>

          <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={saving}
            >
              {saving ? "A guardar..." : (fine ? "Atualizar" : "Registar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Fine Detail Modal Component
const FineDetailModal = ({ fine, onClose, onUpload, onDeleteDocument, onViewDocument, uploading, fileInputRef }) => {
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(fine.fine_id, file);
      e.target.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Detalhes da Coima</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
              <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Vehicle & Driver Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Viatura</h4>
              <p className="text-lg font-mono font-bold text-blue-600">{fine.license_plate}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">{fine.vehicle_info?.brand} {fine.vehicle_info?.model}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Condutor Atribuído</h4>
              <p className="text-lg font-medium text-slate-800 dark:text-white">{fine.assigned_driver_name || "-"}</p>
            </div>
          </div>

          {/* Fine Details */}
          <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Data/Hora</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.fine_date} {fine.fine_time || ""}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Local</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.location || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Tipo de Infração</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.infraction_type || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Entidade</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.authority || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Nº Notificação</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.notification_number || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Prazo Pagamento</p>
              <p className="font-medium text-slate-800 dark:text-white">{fine.payment_deadline || "-"}</p>
            </div>
          </div>

          {/* Description */}
          {fine.infraction_description && (
            <div>
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Descrição</h4>
              <p className="text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/30 p-3 rounded-lg">
                {fine.infraction_description}
              </p>
            </div>
          )}

          {/* Amount & Status */}
          <div className="flex items-center justify-between bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Valor</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{fine.amount?.toFixed(2) || "0.00"} €</p>
              {fine.points_deducted > 0 && (
                <p className="text-sm text-red-500">-{fine.points_deducted} pontos</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Estado</p>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                fine.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                fine.status === 'pending_response' ? 'bg-amber-100 text-amber-700' :
                fine.status === 'awaiting_payment' ? 'bg-blue-100 text-blue-700' :
                'bg-slate-200 text-slate-700'
              }`}>
                {fine.status === 'paid' ? 'Pago' :
                 fine.status === 'pending_response' ? 'Pendente Resposta' :
                 fine.status === 'awaiting_payment' ? 'Aguarda Pagamento' :
                 fine.status === 'salary_deduction_pending' ? 'Dedução Salarial' :
                 fine.status}
              </span>
            </div>
          </div>

          {/* Documents Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Documentos Anexados</h4>
              <label className="cursor-pointer">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      A carregar...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Carregar Auto de Notícia
                    </>
                  )}
                </span>
              </label>
            </div>

            {fine.documents?.length > 0 ? (
              <div className="space-y-2">
                {fine.documents.map((doc, idx) => (
                  <div key={doc.document_id || idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        doc.mime_type?.includes('pdf') ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {doc.mime_type?.includes('pdf') ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-white">{doc.filename}</p>
                        <p className="text-xs text-slate-500">{formatFileSize(doc.file_size)} • {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('pt-PT') : '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onViewDocument(fine.fine_id, doc)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Visualizar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteDocument(fine.fine_id, doc.document_id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                <svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-slate-500 dark:text-slate-400">Sem documentos anexados</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Carregue o auto de notícia (PDF ou imagem)</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {fine.notes && (
            <div>
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Notas</h4>
              <p className="text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/30 p-3 rounded-lg">
                {fine.notes}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinesPage;
